import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ValidationError } from '../../errors/ValidationError';
import { useOnboardingTracking } from '../../hooks/useOnboardingTracking';
import { FieldObject } from '../../openapi/atlantis';
import {
  ApplicationControllerApi,
  ApplicationResponse,
  OnboardingResponseRoleEnum,
} from '../../openapi/yenta';
import ErrorService from '../../services/ErrorService';
import { RootState } from '../../types';
import Logger from '../../utils/Logger';
import {
  formatStateProvince,
  getLegacyApplicationId,
} from '../../utils/OnboardingHelpers';
import { getYentaConfiguration } from '../../utils/OpenapiConfigurationUtils';
import DefaultLoader from '../DefaultLoader';
import PaymentCardForm, { PaymentFees } from '../payment/PaymentCardForm';

interface ApplicationFeeProps {
  field: FieldObject;
  onSubmit: (formData?: any) => void;
  invitationId?: string;
  invitationType?: string;
}

const ApplicationFee: React.FC<ApplicationFeeProps> = ({
  field,
  onSubmit: _onSubmit, // Not used but kept for API compatibility
  invitationId,
  invitationType,
}) => {
  // Get application data from Redux store
  const { authUserDetail, couponCodeDetails } = useSelector(
    (state: RootState) => ({
      authUserDetail: state.auth.authUserDetail,
      couponCodeDetails: state.payment.promoDiscountInfo,
    }),
  );

  // State for Yenta application data
  const [application, setApplication] = useState<ApplicationResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  // Get legacy application ID from user's onboarding
  const legacyApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );

  // Fetch application data from Yenta API
  useEffect(() => {
    const fetchApplication = async () => {
      if (legacyApplicationId) {
        try {
          setIsLoading(true);
          const applicationApi = new ApplicationControllerApi(
            await getYentaConfiguration(),
          );
          const { data } =
            await applicationApi.getApplication(legacyApplicationId);
          setApplication(data);
        } catch (error) {
          ErrorService.notifyIgnoreAuthErrors(
            'Failed to fetch application',
            error as Error,
          );
          // Keep defaults if fetch fails
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    fetchApplication();
  }, [legacyApplicationId]);

  // Extract fee information from application data or field configuration
  const fees = useMemo<PaymentFees>(() => {
    // First try to get fees from the fetched application data
    if (application?.doesBusinessInExtended?.length) {
      const signupFee =
        application.doesBusinessInExtended[0]?.licenseResponse
          ?.administrativeArea?.signupFee?.amount || 149;

      const licenseTransferFees = application.doesBusinessInExtended
        .map((item) => {
          const stateOrProvince =
            item.licenseResponse?.administrativeArea?.stateOrProvince || '';
          const amount =
            item.licenseResponse?.administrativeArea?.licenseTransferFee
              ?.amount || 0;

          if (!stateOrProvince) return null;

          return {
            state: stateOrProvince,
            stateName: formatStateProvince(stateOrProvince),
            amount: amount,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const currency =
        application.doesBusinessInExtended[0]?.licenseResponse
          ?.administrativeArea?.signupFee?.currency || 'USD';

      return {
        applicationFee: signupFee,
        licenseTransferFees,
        currency,
      };
    }

    // Default fallback fees
    return {
      applicationFee: 149,
      licenseTransferFees: [],
      currency: 'USD',
    };
  }, [application]);

  const paymentFormRef = useRef<any>(null);
  const { trackPaymentSubmitted } = useOnboardingTracking();

  // Calculate total to determine if payment is required
  const totalAmount = useMemo(() => {
    const licenseTransferTotal =
      fees.licenseTransferFees?.reduce((sum, fee) => sum + fee.amount, 0) || 0;

    const discountedFee = Math.max(
      0,
      fees?.applicationFee - (couponCodeDetails?.calculatedDiscount || 0),
    );

    return licenseTransferTotal + discountedFee;
  }, [
    couponCodeDetails?.calculatedDiscount,
    fees?.applicationFee,
    fees.licenseTransferFees,
  ]);

  // This function will be called by parent to get payment data
  const getPaymentData = useCallback(async () => {
    const feeWaived = totalAmount < 0.5;

    if (totalAmount >= 0.5 && paymentFormRef.current) {
      // Validate form FIRST before creating payment method
      const isValid = await paymentFormRef.current.validateForm();

      if (!isValid) {
        // Validation failed - errors are already shown to user
        // Return null to signal validation failure without throwing
        return null;
      }

      // Validation passed - proceed with creating payment method
      try {
        const paymentResult =
          await paymentFormRef.current.createPaymentMethod();

        // Track payment submission milestone
        trackPaymentSubmitted('payment_screen', feeWaived, 'Submit Payment');

        return {
          paymentMethodId: paymentResult.paymentMethodId,
          fees: fees,
          promoCode: paymentResult.promoCode,
          zipCode: paymentResult.zipCode,
          totalAmount: totalAmount,
        };
      } catch (error) {
        // Validation errors: user input issues, don't report to Datadog
        if (error instanceof ValidationError) {
          Logger.debug(
            'Payment validation failed (user error):',
            error.message,
          );
          return null; // Signal failure, ConfigDrivenFormV2 exits early
        }

        // System/Stripe API errors: re-throw to be reported to Datadog
        Logger.error('Payment system error:', error);
        throw error; // Propagates to ConfigDrivenFormV2 catch → Datadog
      }
    } else {
      // No payment required (fee waived)
      // Track fee waived milestone
      trackPaymentSubmitted('payment_screen', feeWaived, 'Skip Payment');

      return {
        paymentMethodId: '',
        fees: fees,
        skipped: true,
        promoCode: couponCodeDetails?.couponCode || '',
        totalAmount: totalAmount,
      };
    }
  }, [totalAmount, fees, couponCodeDetails, trackPaymentSubmitted]);

  // Register the getter so parent can call it
  useEffect(() => {
    if (field.id) {
      (window as any)[`getPaymentData_${field.id}`] = getPaymentData;
    }

    return () => {
      if (field.id) {
        delete (window as any)[`getPaymentData_${field.id}`];
      }
    };
  }, [field.id, getPaymentData]);

  return (
    <div className='w-full'>
      <div className='max-w-2xl mx-auto'>
        {isLoading ? (
          <DefaultLoader />
        ) : (
          <PaymentCardForm
            ref={paymentFormRef}
            fees={fees}
            applicationId={legacyApplicationId!}
            invitationId={invitationId}
            invitationType={invitationType as any}
          />
        )}
      </div>
    </div>
  );
};

export default ApplicationFee;
